import { Button, Dropdown, Space } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import { useCapabilityContext, type Capability } from '../capability/CapabilityContext';
import { useConfirmAction, type ConfirmActionOptions } from '../useConfirmAction';

/**
 * The single row-action affordance: a kebab at the end of the row.
 *
 * Has NO Edit and NO View BY CONVENTION. Those were the redundant pair that
 * made every row carry three buttons in a 280px column; opening a record is
 * now the row click itself. What remains here is everything that is NOT
 * "open this record" — archive, restore, reactivate, test, delete. One
 * sanctioned exception exists: the console users list passes an "Edit" action
 * because UserDetailView has no in-place edit yet — to be dropped when
 * workstream B2 folds edit into that page (see apps/console users/list.tsx).
 *
 * Replaces four incompatible idioms that coexisted across the two apps:
 * an Edit+View+Archive button rail, a kebab, inline link buttons, and nothing.
 */
export interface RowAction {
  key: string;
  label: string;
  danger?: boolean;
  /** Omit for an ungated action. */
  cap?: Capability;
  onRun: () => void | Promise<void>;
  /** When set, the action is routed through a confirmation dialog. */
  confirm?: Omit<ConfirmActionOptions, 'onConfirm'>;
}

export interface RowActionsMenuProps {
  actions: RowAction[];
  /** Key of at most one action to promote out of the kebab. */
  primary?: string;
  'aria-label'?: string;
}

/**
 * Reads the predicate ONCE rather than calling useCapability per action.
 *
 * The obvious `actions.filter((a) => useCapability(a.cap))` breaks the rules of
 * hooks: the number of hook calls would vary with the action list, which
 * differs per row (an inactive user gets Reactivate, an active one gets Edit).
 * Preserves the two-axis semantics — `cap === undefined` means allowed.
 */
function useVisibleActions(actions: RowAction[]): RowAction[] {
  const { can } = useCapabilityContext();
  return actions.filter((a) => a.cap === undefined || can(a.cap));
}

export function RowActionsMenu({
  actions,
  primary,
  'aria-label': ariaLabel = 'Actions',
}: RowActionsMenuProps) {
  const confirm = useConfirmAction();
  const visible = useVisibleActions(actions);

  const run = (action: RowAction) => {
    if (action.confirm) {
      confirm({ ...action.confirm, onConfirm: async () => { await action.onRun(); } });
      return;
    }
    void action.onRun();
  };

  if (visible.length === 0) return null;

  const promoted = primary ? visible.find((a) => a.key === primary) : undefined;
  const rest = promoted ? visible.filter((a) => a.key !== promoted.key) : visible;

  return (
    <Space size={4} onClick={(e) => e.stopPropagation()}>
      {promoted && (
        <Button size="small" danger={promoted.danger} onClick={() => run(promoted)}>
          {promoted.label}
        </Button>
      )}
      {rest.length > 0 && (
        <Dropdown
          trigger={['click']}
          menu={{
            items: rest.map((a) => ({
              key: a.key,
              label: a.label,
              danger: a.danger,
              onClick: () => run(a),
            })),
          }}
        >
          <Button
            type="text"
            size="small"
            icon={<EllipsisOutlined />}
            aria-label={ariaLabel}
          />
        </Dropdown>
      )}
    </Space>
  );
}
